// One collapsible category group of cells inside the row-details panel; honors density and caps long groups at GROUP_CELL_CAP with a "+N more" inline expander.

import { useEffect, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { RowCell, type RowCellSmartEditContext } from './RowCell';
import type { RowCellGroup } from '../types/singleRowTypes';

const GROUP_CELL_CAP = 100;

type RowCellsGroupProps = {
  readonly group: RowCellGroup;
  readonly collapsed: boolean;
  readonly density?: 'compact' | 'normal';
  readonly onToggleCollapsed: () => void;
  /** Slice 3 (EP4) — FK cell click handler bubbled up to SingleRowView. */
  readonly onFkClick?: (args: { column: string; value: unknown; modifierKey: boolean }) => void;
  /** When true, cells render an Edit (Pencil) affordance on hover. */
  readonly editable?: boolean;
  /** Edit-commit handler. Forwarded to each RowCell. */
  readonly onEditCommit?: (args: { column: string; oldValue: unknown; newValue: unknown }) => void;
  /**
   * Per-column smart-edit context resolver. Returns FK/enum/boolean context
   * for a given column name, or `undefined` for plain columns.
   */
  readonly resolveSmartEditContext?: (columnName: string) => RowCellSmartEditContext | undefined;
  /** Optional content rendered above the cell grid (T11 — the spatial group's mini map). */
  readonly mapSlot?: ReactNode;
};

export function RowCellsGroup({
  group,
  collapsed,
  density = 'normal',
  onToggleCollapsed,
  onFkClick,
  editable = false,
  onEditCommit,
  resolveSmartEditContext,
  mapSlot,
}: RowCellsGroupProps) {
  const [expandedBeyondCap, setExpandedBeyondCap] = useState(false);
  // Reset the "+N more" expander when the cell list identity changes
  // (e.g. search filter shrinks then clears) so the cap isn't silently bypassed.
  useEffect(() => {
    setExpandedBeyondCap(false);
  }, [group.cells]);

  const totalCells = group.cells.length;
  const overCap = totalCells > GROUP_CELL_CAP;
  const visibleCells =
    overCap && !expandedBeyondCap ? group.cells.slice(0, GROUP_CELL_CAP) : group.cells;
  const hiddenCount = overCap && !expandedBeyondCap ? totalCells - GROUP_CELL_CAP : 0;

  const gridClass =
    density === 'compact'
      ? 'grid grid-cols-1 sm:grid-cols-2 gap-1 px-2 pb-2'
      : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 px-2 pb-2';

  return (
    <section
      className="rounded-md border border-border bg-background/40"
      data-testid={`row-cells-group-${group.key}`}
    >
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
        onClick={onToggleCollapsed}
        aria-expanded={!collapsed}
      >
        <span className="flex items-center gap-1">
          {collapsed ? (
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          <span className="uppercase tracking-wide">{group.label}</span>
          <span className="text-[10px] text-muted-foreground/80">({totalCells})</span>
        </span>
      </button>
      {!collapsed && (
        <>
          {mapSlot}
          <div className={gridClass}>
            {visibleCells.map((cell) => (
              <RowCell
                key={cell.column.name}
                cell={cell}
                density={density}
                onFkClick={onFkClick}
                editable={editable}
                onEditCommit={onEditCommit}
                smartEditContext={resolveSmartEditContext?.(cell.column.name)}
              />
            ))}
          </div>
          {hiddenCount > 0 && (
            <button
              type="button"
              className="w-full px-2 pb-2 text-[11px] text-muted-foreground hover:text-foreground text-left"
              onClick={() => setExpandedBeyondCap(true)}
              data-testid={`row-cells-group-${group.key}-show-more`}
            >
              + {hiddenCount} more cell{hiddenCount === 1 ? '' : 's'} — click to expand
            </button>
          )}
        </>
      )}
    </section>
  );
}
